import { Separator } from "@qw/ui";
import type { FC, ReactElement } from "react";
import type { ProductHistoricalInsights } from "./historical-insights.types";
import { InsightsBoundSection } from "./sections/insights-bound-section";
import { InsightsCarrierContextSection } from "./sections/insights-carrier-context-section";
import { InsightsCharacteristicAppetiteSection } from "./sections/insights-characteristic-appetite-section";
import { InsightsClassContextSection } from "./sections/insights-class-context-section";
import { InsightsDeclinedSection } from "./sections/insights-declined-section";
import { InsightsOperationalSection } from "./sections/insights-operational-section";
import { InsightsOutcomesSection } from "./sections/insights-outcomes-section";
import { InsightsPricingSection } from "./sections/insights-pricing-section";
import { InsightsQuotedSection } from "./sections/insights-quoted-section";
import { InsightsSummarySection } from "./sections/insights-summary-section";

interface Props {
  historicalInsights: unknown;
}

interface SectionEntry {
  key: string;
  hasData: boolean;
  component: ReactElement;
}

function isValidInsightsShape(data: unknown): data is ProductHistoricalInsights {
  if (!data || typeof data !== "object") {
    return false;
  }
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.summary === "object" &&
    obj.summary !== null &&
    typeof obj.outcomes === "object" &&
    obj.outcomes !== null &&
    typeof (obj.outcomes as Record<string, unknown>).lossReasons === "object"
  );
}

export const HistoricalInsightsPanel: FC<Props> = ({ historicalInsights }) => {
  if (!historicalInsights || !isValidInsightsShape(historicalInsights)) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
        No historical insights available yet.
      </div>
    );
  }

  const insights = historicalInsights as ProductHistoricalInsights;

  const lossEntries = Object.entries(insights.outcomes.lossReasons);

  const sectionEntries: SectionEntry[] = [
    {
      key: "summary",
      hasData: true,
      component: <InsightsSummarySection summary={insights.summary} windowMonths={insights.windowMonths} />,
    },
    {
      key: "quoted",
      hasData: insights.quoted.totalCount > 0,
      component: <InsightsQuotedSection quoted={insights.quoted} />,
    },
    {
      key: "bound",
      hasData: insights.bound.totalCount > 0,
      component: <InsightsBoundSection bound={insights.bound} />,
    },
    {
      key: "declined",
      hasData: insights.declined.totalCount > 0,
      component: <InsightsDeclinedSection declined={insights.declined} />,
    },
    {
      key: "carrier",
      hasData:
        insights.carrierContext.appetiteTier != null ||
        insights.carrierContext.carrierSummary != null ||
        insights.carrierContext.redFlags.length > 0 ||
        insights.carrierContext.exclusions.length > 0,
      component: <InsightsCarrierContextSection carrierContext={insights.carrierContext} />,
    },
    {
      key: "operational",
      hasData:
        insights.operational.typicalResponseDays != null ||
        insights.operational.overallFlexibility != null ||
        insights.operational.commonSubjectivities.length > 0 ||
        insights.operational.submissionRequirements.length > 0,
      component: <InsightsOperationalSection operational={insights.operational} />,
    },
    {
      key: "pricing",
      hasData: insights.pricing.carrierRate != null || insights.pricing.marketRate != null,
      component: <InsightsPricingSection pricing={insights.pricing} />,
    },
    {
      key: "class",
      hasData:
        insights.classContext.marketDifficulty != null ||
        insights.classContext.preferredCarriers.length > 0 ||
        insights.classContext.keyExposures.length > 0 ||
        insights.classContext.commonExclusions.length > 0,
      component: <InsightsClassContextSection classContext={insights.classContext} />,
    },
    {
      key: "characteristics",
      hasData: insights.characteristicAppetite.length > 0,
      component: <InsightsCharacteristicAppetiteSection characteristicAppetite={insights.characteristicAppetite} />,
    },
    {
      key: "outcomes",
      hasData:
        insights.outcomes.winFactors.length > 0 ||
        lossEntries.length > 0 ||
        insights.outcomes.competitiveFactors.length > 0,
      component: <InsightsOutcomesSection outcomes={insights.outcomes} />,
    },
  ];

  const visibleSections = sectionEntries.filter((s) => s.hasData);

  return (
    <div className="space-y-4">
      {visibleSections.map((section, i) => (
        <div key={section.key}>
          {i > 0 && <Separator className="mb-4" />}
          {section.component}
        </div>
      ))}
    </div>
  );
};
